import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../shared/components/ui/Button';
import ResponsiveFormLayout from '../../../shared/components/layout/ResponsiveFormLayout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../../../shared/contexts/LanguageContext';

/**
 * Manual fallback (Tier 3/4): automatic QR verification failed (Cloudflare/reCAPTCHA/
 * unavailable). The doctor opens the official IMA page and copies their details. This
 * data is NOT trusted — the account stays pending staff review.
 */
const ManualQrFallbackScreen = () => {
  const enteredLicense = sessionStorage.getItem('login_license_number') || '';
  const [confirmedLoaded, setConfirmedLoaded] = useState(false);
  const [form, setForm] = useState({
    // Locked to the license entered at login — the card's reg number must match it.
    reg_num: enteredLicense,
    full_name_ar: '',
    full_name_en: '',
    mem_prof: '',
    reg_date: '',
    mem_start_date: '',
    mem_end_date: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const authService = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const qrUrl = sessionStorage.getItem('qr_url') || 'https://v.ima.org.iq';
  const uid = (qrUrl.split('/').filter(Boolean).pop() || '').replace(/\D/g, '');

  const set = (k: string, v: string) => { setForm((f) => ({ ...f, [k]: v })); setError(null); };

  const today = new Date().toISOString().split('T')[0];

  const handleSubmit = async () => {
    if (!form.reg_num.trim()) {
      setError(t('qr_manual_reg_required'));
      return;
    }
    // Reject an expired membership — the doctor must have a valid (future) expiry.
    if (form.mem_end_date && form.mem_end_date < today) {
      setError(t('qr_manual_expired'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await authService.qrManualSubmit({
        uid: uid || form.reg_num,
        reg_num: Number(form.reg_num),
        full_name_ar: form.full_name_ar || undefined,
        full_name_en: form.full_name_en || undefined,
        mem_prof: form.mem_prof || undefined,
        reg_date: form.reg_date || undefined,
        mem_start_date: form.mem_start_date || undefined,
        mem_end_date: form.mem_end_date || undefined,
        entered_license: Number(enteredLicense) || undefined,
      });
      if (!result.isSuccess || !result.data) {
        setError(result.error || t('qr_verify_failed'));
        return;
      }
      if (result.data.action === 'qr_manual_pending' && result.data.qr_pending_token) {
        sessionStorage.setItem('qr_pending_token', result.data.qr_pending_token);
        sessionStorage.setItem('qr_prefill', JSON.stringify(result.data.prefill || {}));
        navigate('/qr_phone_entry');
        return;
      }
      // pending_review_no_login: established account → queued for review, no login.
      setInfo(result.data.message || t('qr_manual_pending_review'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('qr_verify_failed'));
    } finally {
      setLoading(false);
    }
  };

  const field = (key: keyof typeof form, label: string, type = 'text', readOnly = false, min?: string) => (
    <div className="mb-3">
      <label className="block text-[#726c6c] text-sm mb-1">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        readOnly={readOnly}
        min={min}
        className={`w-full px-4 py-2.5 text-base border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#2e5d7d] ${
          readOnly ? 'border-gray-200 bg-gray-100 text-gray-500' : 'border-gray-300'
        }`}
        dir={key === 'full_name_ar' ? 'rtl' : 'ltr'}
      />
    </div>
  );

  if (info) {
    return (
      <ResponsiveFormLayout
        showBackButton={false}
        showLanguageToggle={true}
        icon="/ima_logo.png"
        showIcon={true}
        iconSize="w-40 h-32"
        title={t('qr_manual_submitted_title')}
        subtitle=""
        actions={<Button onClick={() => navigate('/login')} text={t('back')} />}
        maxHeight="95vh"
      >
        <div className="w-full text-center text-[#726c6c] text-sm leading-relaxed">{info}</div>
      </ResponsiveFormLayout>
    );
  }

  return (
    <ResponsiveFormLayout
      showBackButton={true}
      onBack={() => navigate('/scan_id')}
      showLanguageToggle={true}
      icon="/ima_logo.png"
      showIcon={true}
      iconSize="w-40 h-32"
      title={t('qr_manual_title')}
      subtitle={t('qr_manual_subtitle')}
      actions={
        <Button
          onClick={handleSubmit}
          text={t('continue')}
          loading={loading}
          disabled={loading || !confirmedLoaded || !form.reg_num}
        />
      }
      maxHeight="95vh"
    >
      <div className="w-full">
        <a
          href={qrUrl}
          target="_blank"
          rel="noreferrer"
          className="block w-full text-center mb-3 px-4 py-3 rounded-2xl border-2 border-[#2e5d7d] text-[#2e5d7d] font-semibold"
        >
          {t('qr_manual_open_page')}
        </a>

        <label className="flex items-center gap-2 mb-4 text-sm text-[#726c6c]">
          <input type="checkbox" checked={confirmedLoaded} onChange={(e) => setConfirmedLoaded(e.target.checked)} />
          {t('qr_manual_confirm_loaded')}
        </label>

        {confirmedLoaded && (
          <div className="w-full">
            {field('reg_num', t('qr_manual_reg_num'), 'text', true)}
            {field('full_name_ar', t('qr_manual_name_ar'))}
            {field('full_name_en', t('qr_manual_name_en'))}
            {field('mem_prof', t('qr_manual_profession'))}
            {field('mem_end_date', t('qr_manual_expiry'), 'date', false, today)}
            <div className="text-xs text-[#a08a3a] bg-[#fff8e1] rounded-xl p-3 mt-2">
              {t('qr_manual_review_note')}
            </div>
          </div>
        )}

        {error && <div className="text-red-500 text-sm mt-2 text-center">{error}</div>}
      </div>
    </ResponsiveFormLayout>
  );
};

export default ManualQrFallbackScreen;
